import React from 'react';
import { Building2, Layers, Sparkles, BadgeCheck, Handshake, RefreshCw, XCircle } from 'lucide-react';
import { Reveal } from '../ui/Reveal';
interface HowMarketplaceWorksProps {
  /** Tailors the audience-facing language in the closing line. */
  audience?: 'brands' | 'marketing agencies' | 'you';
  /** Background treatment so the section sits well on different pages. */
  background?: 'white' | 'muted';
}
const STEPS = [{
  icon: BadgeCheck,
  title: 'We vet every agency',
  body: 'Before an agency can create an account, our team meets with them one-on-one to verify who they are and the talent they represent — and we check in regularly to keep them accountable.'
}, {
  icon: Building2,
  title: 'Agencies manage their own rosters',
  body: 'Each vetted agency maintains its own live roster of creators — updating talent, performance data, pricing, and availability directly. No stale PDFs, no guesswork.'
}, {
  icon: Layers,
  title: 'We pool it into one network',
  body: 'Zano brings every agency roster together into one searchable database — so you can discover thousands of represented creators across hundreds of trusted agencies in one place.'
}];
export const HowMarketplaceWorks = ({
  audience = 'brands',
  background = 'muted'
}: HowMarketplaceWorksProps) => {
  const audienceLabel = audience === 'you' ? 'your agency clients' : audience;
  return <section className={`py-20 md:py-24 ${background === 'muted' ? 'bg-gray-50 border-y border-gray-100' : 'bg-white'}`} data-id="element-1385" data-mp="49vukyldnz2j">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1386" data-mp="3vff1zpgcpc68">
        <Reveal data-id="element-1387" data-mp="3qh3uvmhfxfrp">
          <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-1388" data-mp="3qglp9z8w1p74">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zano-blush text-zano-pink text-xs font-semibold border border-zano-pink/20 mb-5" data-id="element-1389" data-mp="3mhl96ohsp0nt">
              <Sparkles size={13} data-id="element-1390" data-mp="2b1oc2czszafi" /> How the marketplace works
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-zano-charcoal mb-4 leading-tight" data-id="element-1391" data-mp="2b3r6d97znpq1">
              Vetted agency accounts — not scraped from the internet
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1392" data-mp="2hh576kgg4dou">
              Anyone can scrape the internet for creator data. We don't. Zano is
              built on real relationships with high-quality talent agencies who
              manage their own rosters — and we make it easier for them to get
              booked at scale.
            </p>
          </div>
        </Reveal>

        {/* The 3-step flow */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 relative" data-id="element-1393" data-mp="1ykdhntab92cc">
          {STEPS.map((step, idx) => <Reveal key={step.title} delay={idx * 0.1} className="h-full" data-id="element-1394" data-mp="1jsbm2lf9wvi4" data-mpi={idx}>
              <div className="relative h-full bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-1395" data-mp="1otisag1rpmmo">
                <div className="flex items-center gap-3 mb-4" data-id="element-1396" data-mp="1avksqgs3vur8">
                  <div className="w-11 h-11 rounded-xl bg-zano-lavender/60 border border-zano-pink/10 flex items-center justify-center shrink-0" data-id="element-1397" data-mp="1eu0c8aozakzw">
                    <step.icon size={20} className="text-zano-pink" data-id="element-1398" data-mp="w8m38rct7af5" />
                  </div>
                  <span className="text-xs font-bold text-gray-400" data-id="element-1399" data-mp="r9r8qs5hbxf7">
                    Step {idx + 1}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1400" data-mp="107beadadkfvm">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1401" data-mp="1f42gsvfci7pg">
                  {step.body}
                </p>
              </div>
            </Reveal>)}
        </div>

        {/* Scraped vs. Zano contrast */}
        <Reveal delay={0.2} data-id="element-1402" data-mp="1nzrvklmag3tv">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-1403" data-mp="1jvtz6iocl74i">
            <div className="bg-white rounded-2xl p-6 border border-gray-200" data-id="element-1404" data-mp="1xvv90nmlbow4">
              <div className="flex items-center gap-2 mb-3" data-id="element-1405" data-mp="1xts8varq4fdo">
                <XCircle size={18} className="text-gray-400 shrink-0" data-id="element-1406" data-mp="1sycw6m5gcpiu" />
                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-1407" data-mp="2czo71ofzxow6">
                  Scraped databases
                </h4>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-500" data-id="element-1408" data-mp="2gydlzlgonehx">
                <li data-id="element-1409" data-mp="2ls007i2hvqfu">Guessed emails and dead contact info</li>
                <li data-id="element-1410" data-mp="2qr2s00tgs05v">Outdated follower counts and no real performance data</li>
                <li data-id="element-1411" data-mp="1u0acp5ecuc55">No one accountable for the creator or the deal</li>
                <li data-id="element-1412" data-mp="1u23i6hd8eqbk">Cold outreach into a void</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 border-2 border-zano-pink/30 shadow-float" data-id="element-1413" data-mp="1y2e10um4hb7u">
              <div className="flex items-center gap-2 mb-3" data-id="element-1414" data-mp="22zdve89m0pj4">
                <BadgeCheck size={18} className="text-zano-pink shrink-0" data-id="element-1415" data-mp="280l3y9jktsqu" />
                <h4 className="text-sm font-bold text-zano-pink uppercase tracking-wider" data-id="element-1416" data-mp="3eg4726wsy9k4">
                  The Zano network
                </h4>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-700" data-id="element-1417" data-mp="3jdnkj4loeib0">
                <li className="flex items-start gap-2" data-id="element-1418" data-mp="17s4xzmv6fej6">
                  <Handshake size={16} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1419" data-mp="17tps5bdkwvgi" />
                  Verified agencies we've personally met and vetted
                </li>
                <li className="flex items-start gap-2" data-id="element-1420" data-mp="t3ii9k1n0krf">
                  <RefreshCw size={16} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1421" data-mp="t0wc9oueadbb" />
                  Live rosters maintained by the managers themselves
                </li>
                <li className="flex items-start gap-2" data-id="element-1422" data-mp="f2xyolnwekwv">
                  <Building2 size={16} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1423" data-mp="f3g4oa0d4dhz" />
                  Direct lines to the people who represent the talent
                </li>
                <li className="flex items-start gap-2" data-id="element-1424" data-mp="24i4eq4lhdjcu">
                  <BadgeCheck size={16} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1425" data-mp="24innovt5kxeq" />
                  Relationships built to book talent at scale
                </li>
              </ul>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.3} data-id="element-1426" data-mp="3p1hmfygtszra">
          <p className="text-center text-base text-gray-600 max-w-2xl mx-auto mt-10" data-id="element-1427" data-mp="3oy3n6z3gl6o2">
            The agencies manage the talent. We make it easier for them to get
            booked — and pool it all together so{' '}
            <span className="font-semibold text-zano-charcoal" data-id="element-1428" data-mp="qij2hwz7jelv">
              {audienceLabel}
            </span>{' '}
            can discover high-quality, represented creators in one trusted
            place.
          </p>
        </Reveal>
      </div>
    </section>;
};